<script setup lang="ts">
  /**
   * AdSlot — real ad slot with a prototyping fallback.
   *
   * Renders the platform's AdHandler (common layer) when:
   *  1. `placement` is set to a real key from the tenant's
   *     ad_ops/adPlacementMappings.ts, and
   *  2. the hosting app provides the common ad stack — detected via the
   *     `devDisableAds` runtime config key that common/nuxt.config.ts
   *     defines, whose value (the platform's dev ads-off switch) is
   *     honored too.
   *
   * Outside a tenant app (standalone lego-land dev) it renders a fixed-size
   * dashed placeholder instead. The <AdHandler> branch is never rendered
   * there, so the unresolved component is never mounted.
   *
   * In-feed native ads are NOT this component — see PortalFeedAd, which
   * renders dplxapi creatives as article rows.
   */
  import { computed, onMounted, ref } from 'vue'
  import { useMediaQuery } from '@vueuse/core'
  import useAdFunctions from '../../composables/ad-ops/useAdFunctions'
  import type { AdProviderType } from '../../ad_ops/providers/types'
  import {
    minWidthQuery,
    type ScreenName,
  } from '../../../lego-land/layout-system/screens'

  export interface AdSlotProps {
    /** Placement key from the tenant's ad_ops/adPlacementMappings.ts */
    placement?: string
    /** Forwarded to AdHandler, which routes it to the provider component */
    adType?: AdProviderType
    lazyRender?: boolean
    /** Fallback box + reserved slot dimensions */
    width?: number
    height?: number
    label?: string
    /**
     * Drop the reserved min-height once a creative renders (default).
     * Pass false for zones that must keep their full reserve regardless
     * of the served creative's size — e.g. a refreshing leaderboard that
     * can receive a bigger ad on the next refresh.
     */
    hugCreative?: boolean
    /**
     * Placement to use below the tablet breakpoint. Ad units are sized per
     * placement, so a desktop unit cannot serve into a phone viewport: the
     * platform's answer is a parallel `_mb` placement, the way AutoLayout and
     * ArticleStreamItem pick `newtab_leaderboard`/`article_hero` plus the
     * suffix. Left unset, the same placement is used at every width.
     */
    mobilePlacement?: string
    /**
     * Do not mount the slot at all below this breakpoint (the layout
     * system's tablet or desktop screen, from lego-land's SCREENS). For
     * slots whose surrounding markup is display:none on smaller widths
     * (the portal rails, the strip): CSS hiding alone is not enough,
     * because a placement whose mapping sets `props.lazyRender: false`
     * renders immediately on mount and fires a request the hidden zone
     * can never fill — a surplus request the exchange counts and we
     * cannot monetize. Left unset, the slot mounts at every width.
     */
    minViewport?: ScreenName
  }

  const props = withDefaults(defineProps<AdSlotProps>(), {
    placement: '',
    adType: 'assertive',
    lazyRender: false,
    width: 728,
    height: 90,
    label: 'Advertisement',
    hugCreative: true,
    mobilePlacement: '',
    minViewport: undefined,
  })

  const { isMobile } = useAdFunctions()

  /** The unit actually requested, once the viewport is known */
  const activePlacement = computed(() =>
    props.mobilePlacement && isMobile.value
      ? props.mobilePlacement
      : props.placement
  )

  // The key exists on every tenant (common defines it unconditionally), so
  // the value must be tested, not the presence: the platform's dev ads-off
  // switch has to reach these slots too. Standalone lego-land has no such
  // key and falls to the placeholder branch.
  const { devDisableAds } = useRuntimeConfig().public
  const hasAdStack = devDisableAds !== undefined && !devDisableAds

  /**
   * Slots with a mobile pairing hold their first request for one tick:
   * useWindowSize cannot know the real width before mount, so rendering
   * immediately would fire a desktop request that the remount on phones
   * throws away — a surplus request the exchange counts and we cannot fill.
   * The wrapper's reserved min-height holds the space while it waits.
   * Slots without a pairing render right away, nothing about their unit
   * depends on width.
   */
  const viewportKnown = ref(false)
  onMounted(() => {
    viewportKnown.value = true
  })

  /**
   * Gated slots render nothing on the server and keep rendering nothing
   * until mounted, so hydration always matches; the slot then appears only
   * at allowed widths.
   */
  const minViewportMatches = props.minViewport
    ? useMediaQuery(minWidthQuery(props.minViewport))
    : ref(true)
  const viewportAllowed = computed(
    () =>
      !props.minViewport || (viewportKnown.value && minViewportMatches.value)
  )

  const showRealAd = computed(
    () => Boolean(activePlacement.value) && hasAdStack && viewportAllowed.value
  )
</script>

<template>
  <!-- Reserve the slot size to avoid layout shift while the ad loads;
       by default the reserve drops once a creative renders (hugCreative) -->
  <div
    v-if="showRealAd"
    class="flex max-w-full items-center justify-center overflow-hidden [&>*:has(a,iframe)]:!h-auto"
    :class="hugCreative ? '[&:has(a,iframe)]:!min-h-0' : ''"
    :style="{ minHeight: `${height}px` }"
  >
    <!-- Keyed by placement: ad providers request their unit once, on mount,
         so if the viewport crosses the breakpoint the handler must remount
         to request the right unit. viewportKnown keeps paired slots from
         mounting at all until the width is real. -->
    <AdHandler
      v-if="!mobilePlacement || viewportKnown"
      :key="activePlacement"
      :placement="activePlacement"
      :ad-type="adType"
      :lazy-render="lazyRender"
      :disable-style-wrapper="true"
    />
  </div>

  <div
    v-else-if="viewportAllowed"
    class="flex max-w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[var(--yp-border)] bg-[var(--yp-hover)] text-content-tertiary"
    :style="{ width: `${width}px`, height: `${height}px` }"
  >
    <span class="text-[10px] uppercase tracking-[0.2em]">{{ label }}</span>
    <span class="text-xs opacity-70">{{ width }}&times;{{ height }}</span>
  </div>
</template>
